import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import Card from "../components/Card";
import CourseCard from "../components/CourseCard";
import EditProfile from "../components/EditProfile";
import "./Profile.css";
import API from "../utils/axiosInstance";
import { FaRegEdit, FaGraduationCap, FaPhone } from "react-icons/fa";
import { useTranslation } from "react-i18next";

const Profile = () => {
  const { t ,i18n} = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [user, setUser] = useState({});
  const [transactions, setTransactions] = useState([]);
  const [completedCourses, setCompletedCourses] = useState([]);
  const [inProgressCourses, setInProgressCourses] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [scheduleList, setScheduleList] = useState([]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await API.get("/users/profile");
        setUser(res.data);
      } catch (err) {
        console.error("Failed to fetch profile:", err);
      }
    };
    fetchProfile();
  }, []);

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const res = await API.get("/transactions");
        setTransactions(res.data);
      } catch (err) {
        console.error("Error fetching transactions:", err);
      }
    };
    fetchTransactions();
  }, []);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await API.get("/courses");
        const allCourses = response.data;
        const completed = allCourses.filter(course => course.status === "completed");
        const inProgress = allCourses.filter(course => course.status === "in_progress");

        setCompletedCourses(completed);
        setInProgressCourses(inProgress);
      } catch (error) {
        console.error("Failed to fetch courses:", error);
      }
    };

    fetchCourses();
  }, []);

  const fetchUserSchedules = async (courseId, userId) => {
    try {
      const res = await API.get(`/schedules/user/${userId}`);
      const allUserSchedules = res.data;

      const courseSchedules = allUserSchedules.filter(s => s.schedule?.courseId === courseId);
      setScheduleList(courseSchedules);
    } catch (err) {
      console.error("Failed to fetch user-specific schedule", err);
    }
  };

  const handleCourseClick = async (course) => {
    setSelectedCourse(course);
    const user = JSON.parse(localStorage.getItem("user"));
    if (user?.id) {
      await fetchUserSchedules(course.id, user.id);
    }
  };

  return (
 <div
      className="dashboard-wrapper"
      dir={['ar', 'fa', 'ir', 'he'].includes(i18n.language) ? 'rtl' : 'ltr'}
      style={{
        flexDirection: ['ar', 'fa', 'ir', 'he'].includes(i18n.language)
          ? 'row-reverse'
          : 'row',
      }}
    >      <div className="sidebar-fixed">
        <Sidebar isOpen={isSidebarOpen} />
      </div>

      <div className="dashboard-main">
        <Navbar onToggleSidebar={() => setIsSidebarOpen(prev => !prev)} />

        <div className="profile-grid">
          {/* Left: Profile */}
          <div className="profile-section">
            <div className="profile-card">
              <div className="profile-header">
                <h4>{t("UserProfile")}</h4>
                <button className="edit-btn" onClick={() => setIsEditing(true)}>
                  <FaRegEdit />
                </button>
              </div>

              <div className="profile-image">
                <img
                  src={user.profileImage || "/images/profile.svg"}
                  alt="Profile"
                  className="profile-image"
                />
              </div>
              <h5 className="profile-name">{user.name || "—"}</h5>
              <p className="profile-email">{user.email || "—"}</p>
              <p className="profile-phone">
                <FaPhone size={30} /> {user.phone || "—"}
              </p>

              <div className="certificates-box">
                <div className="certificates-content">
                  <FaGraduationCap size={35} />
                  <span>{t("Certificates")}</span>
                </div>
                <h4>{user.certificates || "0"}</h4>
              </div>
            </div>
          </div>

          {/* Right: Course Summary */}
          <div className="stats-courses">
            <div className="stats-section">
              <Card title={t("CompletedCourses")} value={completedCourses.length} color="#4CBC5B" />
              <Card title={t("CoursesInProgress")} value={inProgressCourses.length} color="#FFC13D" />
            </div>

            <div className="section-header">
              <h4>{t("CoursesInProgress")}</h4>
              <a href="/courses" className="view-all">{t("ViewAll")}</a>
            </div>
            <div className="courses-grid">
              {inProgressCourses.length > 0 ? (
                inProgressCourses.map(course => (
                  <CourseCard key={course.id} course={course} onClick={handleCourseClick} />
                ))
              ) : (
                <p style={{ color: "#999", fontStyle: "italic" }}>{t("NoInProgressCourses")}</p>
              )}
            </div>
          </div>
        </div>

        {/* Transactions */}
        <div className="section-header">
          <h4>{t("LatestTransactions")}</h4>
          <a href="/transactions" className="view-all">{t("ViewAll")}</a>
        </div>
        <div className="transactions-container">
          <table className="transactions-table">
            <thead>
              <tr>
                <th>{t("Transactions")}</th>
                <th>ID</th>
                <th>{t("Date")}</th>
                <th>{t("Amount")}</th>
                <th>{t("Status")}</th>
              </tr>
            </thead>
            <tbody>
              {transactions.slice(0, 2).map((tx, index) => (
                <tr key={index}>
                  <td>{tx.method}</td>
                  <td>{tx.transactionId}</td>
                  <td>{new Date(tx.date).toLocaleDateString()}</td>
                  <td>${tx.amount ? Number(tx.amount).toFixed(2) : "0.00"}</td>
                  <td className={`status ${tx.status.toLowerCase()}`}>{tx.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selectedCourse && (
        <div className="modal-overlay" onClick={() => setSelectedCourse(null)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <h4>{selectedCourse.title} – {t("Schedule")}</h4>
            {scheduleList.length > 0 ? (
              <ul className="schedule-list">
                {scheduleList.map((s) => (
                  <li key={s.id}>
                    <strong>{s.schedule.title}</strong><br />
                    📅 {s.schedule.date} ⏰ {s.schedule.time}<br />
                    🔗 <a href={s.schedule.joinLink} target="_blank" rel="noreferrer">{t("joinClass")}</a>
                  </li>
                ))}
              </ul>
            ) : (
              <p>{t("NoSessionsScheduled")}</p>
            )}
            <button onClick={() => setSelectedCourse(null)}>{t("Close")}</button>
          </div>
        </div>
      )}

      {isEditing && (
        <EditProfile
          user={user}
          onClose={() => setIsEditing(false)}
          onUpdate={(updatedUser) => setUser(updatedUser)}
        />
      )}
    </div>
  );
};

export default Profile;
